import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NotesService } from '../../services/notes.service';
import { Note, NoteType } from '../../models/note.model';
import { DonorJourneySection } from '../../models/donor-journey-section.model';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-notes-modal',
  templateUrl: './notes-modal.component.html',
  styleUrl: './notes-modal.component.scss'
})
export class NotesModalComponent implements OnInit, OnDestroy {

  @Input() noteType!: 'Note' | 'Summary';
  @Input() noteId = '';
  @Input() editorContent = ''; // Content passed to or edited in the modal
  @Input() section!: DonorJourneySection;
  @Input() generationNoteType?: NoteType;
  @Input() isComprehensiveQuestionnaireCompleted = true;

  quillModules: any;
  private quill!: any;

  isSaving = false;
  isGenerating = false;
  isAutoSaving = false;

  private autoSaveTimerId?: ReturnType<typeof setInterval>;
  private autoSaveSubscription?: Subscription;
  private lastSavedContent = '';
  lastAutoSaveAt?: Date;
  private lastSaveAction?: 'edit' | 'create';
  private lastSavedNote?: Note;
  private readonly epicEligibleSections = new Set([
    "transplant coordinator",
    "transplant nephrologist",
    "dietician",
    "social worker",
    "transplant surgeon",
  ]);

  constructor(public activeModal: NgbActiveModal, private notesService: NotesService) {
    // Use a custom toolbar container so we can add table controls
    this.quillModules = {
      toolbar: {
        container: '#notes-toolbar',
        handlers: {
          table: () => {
            if (!this.quill) return;
            const table = this.quill.getModule('table');
            table.insertTable(3, 3);
          },
          insertRowAbove: () => this.withTable((t: any) => t.insertRowAbove()),
          insertRowBelow: () => this.withTable((t: any) => t.insertRowBelow()),
          insertColumnLeft: () => this.withTable((t: any) => t.insertColumnLeft()),
          insertColumnRight: () => this.withTable((t: any) => t.insertColumnRight()),
          deleteRow: () => this.withTable((t: any) => t.deleteRow()),
          deleteColumn: () => this.withTable((t: any) => t.deleteColumn()),
          deleteTable: () => this.withTable((t: any) => t.deleteTable()),
          applyColor: (value: string) => {
            if (!this.quill) return;
            const v = !value || value === 'default' ? false : value;
            this.quill.format('color', v);
          },
          applyAlign: (value: string) => {
            if (!this.quill) return;
            // Quill default alignment is left (unset). Use false to clear.
            const v = value === 'left' || !value ? false : value;
            this.quill.format('align', v);
          },
        }
      },
      table: true
    };
  }

  shouldShowEpicActions(): boolean {
    if (this.noteType !== "Note") {
      return false;
    }

    const title = this.section?.title?.trim().toLowerCase() ?? "";
    return this.epicEligibleSections.has(title);
  }

  canPullFromNote(): boolean {
    if (this.noteType !== "Summary") {
      return false;
    }

    return this.getExtractedOverallImpression() !== null;
  }

  pullFromNote(): void {
    const extractedContent = this.getExtractedOverallImpression();
    if (extractedContent) {
      this.editorContent = extractedContent;
    }
  }

  private getNoteContent(): string {
    const note = this.section?.notes?.find((entry) => entry.noteType === "Note");
    return note?.content ?? "";
  }

  private getExtractedOverallImpression(): string | null {
    const noteContent = this.getNoteContent();
    if (!noteContent.trim().length) {
      return null;
    }

    return this.extractOverallImpressionFromNote(noteContent);
  }

  private extractOverallImpressionFromNote(noteContent: string): string | null {
    const parser = new DOMParser();
    const doc = parser.parseFromString(noteContent, "text/html");
    const labelRegex = /overall\s*impression\s*\/\s*plan\b/i;
    const elements = Array.from(doc.body.children);

    let headerEl: Element | null = null;
    let headerText = "";
    for (const element of elements) {
      const text = (element.textContent ?? "").trim();
      if (!text) {
        continue;
      }
      if (labelRegex.test(text)) {
        headerEl = element;
        headerText = text;
        break;
      }
    }

    if (!headerEl) {
      return null;
    }

    const collectedLines: string[] = [];
    const headerMatch = headerText.match(labelRegex);
    if (headerMatch) {
      const labelEndIndex = headerMatch.index! + headerMatch[0].length;
      const afterLabel = headerText.slice(labelEndIndex).trim();
      const cleaned = afterLabel.replace(/^[:\-\s]+/, "").trim();
      if (cleaned.length > 0) {
        collectedLines.push(cleaned);
      }
    }

    let current = headerEl.nextElementSibling;
    while (current) {
      const text = (current.textContent ?? "").trim();
      if (!text) {
        break;
      }
      collectedLines.push(text);
      current = current.nextElementSibling;
    }

    if (collectedLines.length === 0) {
      return null;
    }

    const html = collectedLines
      .map((line) => this.escapeHtml(line))
      .join("<br>");
    return `<p>${html}</p>`;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  ngOnInit(): void {
    this.lastSavedContent = this.editorContent ?? '';
    this.startAutoSaveTimer();
  }

  ngOnDestroy(): void {
    this.clearAutoSaveTimer();
    this.autoSaveSubscription?.unsubscribe();
  }

  onEditorCreated(editor: any) {
    this.quill = editor;
    // Quill transforms select pickers (color, align) into runtime DOM elements.
    // Add native title attributes to their visible labels so hover shows a tooltip.
    setTimeout(() => this.addPickerTitles(), 0);
  }

  private withTable(fn: (table: any) => void) {
    if (!this.quill) return;
    const table = this.quill.getModule('table');
    if (!table) return;
    fn(table);
  }

  private addPickerTitles() {
    const toolbarEl = document.querySelector('#notes-toolbar');
    if (!toolbarEl) return;
    // No longer using Quill pickers for color/align; custom buttons provide tooltips.
  }

  generateNote() {
    if (!this.section?.userId || !this.noteType) {
      return;
    }
    this.isGenerating = true;
    const templateType = this.generationNoteType || "";
    this.notesService
      .getPopulatedNote(this.section.userId, templateType, 'html')
      .subscribe({
        next: (html) => {
          this.editorContent = html ?? '';
          this.isGenerating = false;
        },
        error: (err) => {
          console.error('Failed to generate note:', err);
          this.isGenerating = false;
        },
      });
  }

  save() {
    if (this.isSaving || this.isAutoSaving) {
      return;
    }

    this.isSaving = true;
    
    this.editorContent = this.editorContent ?? "";

    if(this.noteId && this.noteId.length > 0){
      this.notesService.updateNote(this.noteId, this.editorContent).subscribe({
        next: (result)=> {
          this.updateLastSavedState(this.editorContent, 'edit', result);
          this.activeModal.close({action: "edit", content: this.editorContent, note: result});
          this.isSaving = false;
        }, 
        error: (error)=> {
          console.error(error);
          this.isSaving = false;
          this.activeModal.close("Error");
        }
      });
    } else {
      const newNote: Partial<Note> = {
        donorJourneySectionId: this.section.userJourneySectionId,
        donorUserId: this.section.userId,
        noteType: this.noteType,
        content: this.editorContent,
      }

      this.notesService.createNote(newNote).subscribe({
        next: (result)=> {
          this.isSaving = false;
          if (result.noteId) {
            this.noteId = result.noteId;
          }
          this.updateLastSavedState(this.editorContent, 'create', result);
          // Return the created note from the API so it includes noteId
          this.activeModal.close({action: "create", content: this.editorContent, note: result});
          
        }, 
        error: (error)=> {
          console.error(error);
          this.isSaving = false;
          this.activeModal.close("Error");
        }
      });
    }
  }

  close() {
    const payload = this.buildClosePayload();
    this.clearAutoSaveTimer();
    this.autoSaveSubscription?.unsubscribe();
    this.autoSaveSubscription = undefined;
    if (payload) {
      this.activeModal.close(payload);
    } else {
      this.activeModal.dismiss();
    }
  }

  private startAutoSaveTimer() {
    this.clearAutoSaveTimer();
    this.autoSaveTimerId = setInterval(() => this.tryAutoSave(), 2 * 60 * 1000);
  }

  private clearAutoSaveTimer() {
    if (this.autoSaveTimerId) {
      clearInterval(this.autoSaveTimerId);
      this.autoSaveTimerId = undefined;
    }
  }

  private tryAutoSave() {
    const currentContent = this.editorContent ?? '';
    const contentChanged = currentContent !== this.lastSavedContent;
    if (!contentChanged || this.isSaving || this.isGenerating || this.isAutoSaving) {
      return;
    }

    if (this.autoSaveSubscription && !this.autoSaveSubscription.closed) {
      return;
    }

    this.isAutoSaving = true;

    if (this.noteId && this.noteId.length > 0) {
      this.autoSaveSubscription = this.notesService.updateNote(this.noteId, currentContent).subscribe({
        next: (result) => {
          this.updateLastSavedState(currentContent, 'edit', result);
          this.isAutoSaving = false;
          this.autoSaveSubscription = undefined;
        },
        error: (error) => {
          console.error('Auto-save failed:', error);
          this.isAutoSaving = false;
          this.autoSaveSubscription = undefined;
        }
      });
      return;
    }

    if (!this.section?.userJourneySectionId || !this.section?.userId || !this.noteType) {
      this.isAutoSaving = false;
      return;
    }

    const newNote: Partial<Note> = {
      donorJourneySectionId: this.section.userJourneySectionId,
      donorUserId: this.section.userId,
      noteType: this.noteType,
      content: currentContent,
    };

    this.autoSaveSubscription = this.notesService.createNote(newNote).subscribe({
      next: (result) => {
        if (result.noteId) {
          this.noteId = result.noteId;
        }
        this.updateLastSavedState(currentContent, 'create', result);
        this.isAutoSaving = false;
        this.autoSaveSubscription = undefined;
      },
      error: (error) => {
        console.error('Auto-save failed:', error);
        this.isAutoSaving = false;
        this.autoSaveSubscription = undefined;
      }
    });
  }

  private updateLastSavedState(content: string, action: 'edit', note?: Note): void;
  private updateLastSavedState(content: string, action: 'create', note: Note): void;
  private updateLastSavedState(content: string, action: 'edit' | 'create', note?: Note) {
    this.lastSavedContent = content;
    this.lastAutoSaveAt = new Date();
    this.lastSaveAction = action;
    this.lastSavedNote = note;
  }

  private buildClosePayload():
    | { action: 'edit'; content: string; note?: Note }
    | { action: 'create'; content: string; note: Note }
    | undefined {
    if (!this.lastSaveAction) {
      return undefined;
    }

    if (this.lastSaveAction === 'create') {
      if (!this.lastSavedNote) {
        return undefined;
      }
      return {
        action: 'create' as const,
        content: this.lastSavedContent,
        note: this.lastSavedNote,
      };
    }

    return {
      action: 'edit' as const,
      content: this.lastSavedContent,
      note: this.lastSavedNote,
    };
  }

}
